import moment from 'moment';
import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH
} from 'utils/constants';
import {
  ERROR_DISPLAY_NAME_TOO_LONG,
  ERROR_DISPLAY_NAME_TOO_SHORT,
  ERROR_EMAILS_DO_NOT_MATCH,
  ERROR_INVALID_DISPLAY_NAME,
  ERROR_PASSWORDS_DO_NOT_MATCH,
  ERROR_PASSWORD_MUST_CONTAIN_LOWERCASE,
  ERROR_PASSWORD_MUST_CONTAIN_NUMBER,
  ERROR_PASSWORD_MUST_CONTAIN_UPPERCASE,
  ERROR_PASSWORD_TOO_LONG,
  ERROR_PASSWORD_TOO_SHORT,
  VALIDATION_ERROR_INVALID_EMAIL,
  VALIDATION_ERROR_INVALID_PASSWORD,
  VALIDATION_ERROR_INVALID_UUID
} from 'utils/messages';
import { collapseWhitespace } from 'utils/strings';

// See https://stackoverflow.com/a/1373724 or https://tools.ietf.org/html/rfc2822#section-3.4.1
const EMAIL_FORMAT_RFC_2822 =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

// See https://stackoverflow.com/questions/7905929/how-to-test-valid-uuid-guid or https://www.ietf.org/rfc/rfc4122.txt
const UUID_FORMAT_RFC_4122 =
  /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

export const emailRequired = value =>
  value ? undefined : VALIDATION_ERROR_INVALID_EMAIL;

export const uuidRequired = value =>
  value ? undefined : VALIDATION_ERROR_INVALID_UUID;

export const passwordRequired = value =>
  value ? undefined : VALIDATION_ERROR_INVALID_PASSWORD;

export const required = value => (value ? undefined : 'Required');

export const trimmedRequired = value => required(value && value.trim());

export const number = value =>
  value && isNaN(Number(value)) ? 'Must be a number' : undefined;

export const email = value =>
  !value || typeof value !== 'string' ? 'Required' : emailFormat(value);

export const uuid = value =>
  !value || typeof value !== 'string' ? 'Required' : uuidFormat(value);

export const emailFormat = value =>
  value && !EMAIL_FORMAT_RFC_2822.test(value)
    ? 'Invalid email address'
    : undefined;

export const uuidFormat = value =>
  value && !UUID_FORMAT_RFC_4122.test(value) ? 'Invalid prize code' : undefined;

export const confirmEmail = (value, confirm) => {
  if (!confirm || typeof confirm != 'string') {
    return 'Required';
  }
  return value === confirm ? undefined : ERROR_EMAILS_DO_NOT_MATCH;
};

export const reduxConfirmEmail = (confirm, allValues) =>
  confirmEmail(allValues && allValues.get('email'), confirm);

export const trimmedEmail = value => email(value && value.trim());
export const trimmedEmailFormat = value => emailFormat(value && value.trim());

const EMPLOYEE_EMAIL_DOMAINS = [
  'windcreek.com',
  'arubarenaissance.com',
  'carnavalcasinocuracao.com',
  'crystalcasinos.aw',
  'curacaorenaissance.com',
  'starbucks.aw'
];
export const employeeEmail = value => {
  value = value && value.trim();
  const emailError = email(value);
  if (emailError) {
    return emailError;
  }
  const domain = value.substring(value.indexOf('@') + 1).toLowerCase();
  return EMPLOYEE_EMAIL_DOMAINS.includes(domain)
    ? undefined
    : 'Must use employee email';
};

export const alphaNumeric = value =>
  value && /[^a-zA-Z0-9 ]/i.test(value)
    ? 'Only alphanumeric characters'
    : undefined;

export const date = (year, month, day) => {
  year = Number(year);
  month = Number(month);
  day = Number(day);
  if (!year || !month || !day) {
    return 'Required';
  }
  return moment([year, month - 1, day]).isValid() ? undefined : 'Invalid date';
};

// Factory functions for validators.
export const createMaxLength = max => value =>
  value && value.length > max ? `Must be ${max} characters or less` : undefined;

export const createMinLength = min => value =>
  value && value.length < min ? `Must be ${min} characters or more` : undefined;

export const createMinValue = min => value =>
  value && value < min ? `Must be at least ${min}` : undefined;

export const password = pass => {
  if (!pass || typeof pass !== 'string') {
    return 'Required';
  }
  return (
    [
      pass.length >= PASSWORD_MIN_LENGTH || ERROR_PASSWORD_TOO_SHORT,
      pass.length <= PASSWORD_MAX_LENGTH || ERROR_PASSWORD_TOO_LONG,
      /[a-z]/.test(pass) || ERROR_PASSWORD_MUST_CONTAIN_LOWERCASE,
      /[A-Z]/.test(pass) || ERROR_PASSWORD_MUST_CONTAIN_UPPERCASE,
      /[0-9]/.test(pass) || ERROR_PASSWORD_MUST_CONTAIN_NUMBER
    ]
      .filter(r => typeof r === 'string')
      .join('\n') || undefined
  );
};

export const confirmPassword = (pass, confirm) => {
  if (!confirm || typeof confirm !== 'string') {
    return 'Required';
  }
  return pass === confirm ? undefined : ERROR_PASSWORDS_DO_NOT_MATCH;
};

export const reduxConfirmPassword = (confirm, allValues) =>
  confirmPassword(allValues && allValues.get('password'), confirm);

export const displayName = name => {
  if (!name || typeof name !== 'string') {
    return 'Required';
  }
  name = normalizeDisplayName(name);
  return (
    [
      name.length >= DISPLAY_NAME_MIN_LENGTH || ERROR_DISPLAY_NAME_TOO_SHORT,
      name.length <= DISPLAY_NAME_MAX_LENGTH || ERROR_DISPLAY_NAME_TOO_LONG,
      // Allow Basic Latin and Latin-1 Supplement letters, numbers, and spaces between words
      /^([a-zA-Z0-9\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff]+)( [a-zA-Z0-9\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff]+)*$/.test(
        name
      ) || ERROR_INVALID_DISPLAY_NAME
    ]
      .filter(r => typeof r === 'string')
      .join('\n') || undefined
  );
};

export const normalizeDisplayName = name => {
  // Normalize unicode characters to precomposed/unformatted characters so they
  // count as a single character and match the expected code point ranges.
  return name.normalize ? name.normalize('NFKC') : name;
};

export const zipCode = (zipCode, allValues) => {
  if (allValues.get('country') === 'USA') {
    if (zipCode?.length !== 5 || isNaN(Number(zipCode))) {
      return 'Must be a 5-digit number';
    }
  }
  if (!zipCode) {
    return undefined;
  }
  if (zipCode.length > 10) {
    return 'Must be 10 characters or less';
  }
  return undefined;
};

export const trimmedDisplayName = name => displayName(collapseWhitespace(name));

export const rewardsNumber = value => {
  if (!value) {
    return 'Required';
  }
  return rewardsNumberFormat(value);
};

export const rewardsNumberFormat = value => {
  return !value || /^[0-9]{13}$/.test(value)
    ? undefined
    : 'Must be a 13-digit number';
};

const validateAccountNumber = value => {
  // ACCOUNT_NUMBER_LENGTH = 13. Must have prefix: 305
  return /^305\d{10}$/.test(value)
    ? undefined
    : 'Account number must start with 305 and be 13 digits long.';
};

const validateCardNumber = value => {
  // CARD_NUMBER_LENGTH = 17; Must have prefix: 305
  return /^305\d{14}$/.test(value)
    ? undefined
    : 'Card number must start with 305 and be 17 digits long.';
};

const validateIGTCardNumber = value => {
  // IGT_CARD_NUMBER_LENGTH = 20; All digits allowed
  return /^\d{20}$/.test(value)
    ? undefined
    : 'IGT card number must be 20 digits long.';
};

const validatePlayerId = value => {
  // PLAYER_ID_LENGTH = 8; Only Upper/lower case letters allowed
  return /^[A-Za-z]{8}$/.test(value)
    ? undefined
    : 'Player ID must be 8 letters long.';
};

export const rewardsPlsNumber = value => {
  if (!value) {
    return 'Required';
  }

  const validators = [
    validateAccountNumber,
    validateCardNumber,
    validateIGTCardNumber,
    validatePlayerId
  ];

  const isValid = validators.some(validator => validator(value) === undefined);

  if (!isValid) {
    const messages = validators
      .map(validator => validator(value))
      .filter(message => message !== undefined);
    return messages.join('\n');
  }
  return undefined;
};

export const rewardsPin = value => {
  if (!value) {
    return 'Required';
  }
  return /^[0-9]{4}$/.test(value) ? undefined : 'Must be a 4-digit number';
};

export const phoneNumber = value => {
  if (!value) {
    return undefined;
  }
  const regex = value.startsWith('+')
    ? /^\+\d{4,15}$/
    : /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
  return regex.test(value)
    ? undefined
    : 'Must be in XXX-XXX-XXXX format for US numbers or begin with a plus (+) for international numbers';
};

export const termsOfService = value => {
  return value ? undefined : 'You must agree to use this site';
};

export const birthday = monthDayYearString => {
  if (!monthDayYearString || typeof monthDayYearString !== 'string') {
    return 'Required';
  }
  // Expects MM/DD/YYYY format
  const regex = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/;
  const parts = monthDayYearString.match(regex);
  if (!parts || parts.length < 4) {
    return 'Required format: MM/DD/YYYY';
  }
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  const year = Number(parts[3]);
  if (!year || !month || !day) {
    return 'Required';
  }
  const date = moment([year, month - 1, day]);
  if (!date.isValid()) {
    return 'Invalid date';
  }
  if (date > moment().subtract(21, 'years')) {
    return 'You must be at least 21 years old';
  }

  return undefined;
};

export const numbersOnlyParse = value => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  return value.replace(/[^0-9]/g, '');
};

export const phoneNumberParse = value => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  // Check for international format
  if (value.startsWith('+')) {
    return '+' + value.replace(/[^0-9]/g, '');
  }

  let result = value.replace(/[ ./]/g, '-');
  result = result.replace(/[^0-9-]/g, '');

  // Test for autocomplete or pasted entries in [1]5551231234 format
  let isoMatch = /^1?-?(\d{3})-?(\d{3})-?(\d{4})$/.exec(result);
  if (isoMatch && isoMatch.length === 4) {
    return isoMatch[1] + '-' + isoMatch[2] + '-' + isoMatch[3];
  }

  let parts = result.split('-');
  if (parts.length === 1 && parts[0].length === 4) {
    parts.push(parts[0].slice(3));
    parts[0] = parts[0].slice(0, 3);
  } else if (
    parts.length === 2 &&
    parts[1].length === 4 &&
    parts[0].length === 3
  ) {
    parts.push(parts[1].slice(3));
    parts[1] = parts[1].slice(0, 3);
  }

  return parts.join('-');
};

export const birthdayParse = value => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  let result = value.replace(/[ .-]/g, '/');
  result = result.replace(/[^0-9/]/g, '');

  // Test for autocomplete or pasted entries in ISO YYYY-MM-DD format
  let isoMatch = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(result);
  if (isoMatch && isoMatch.length === 4) {
    return isoMatch[2] + '/' + isoMatch[3] + '/' + isoMatch[1];
  }

  // Otherwise parse as the date is typed in MM/DD/YYYY
  let parts = result.split('/');
  if (parts.length === 1) {
    if (parts[0].length === 3) {
      parts.push(parts[0].slice(2));
      parts[0] = parts[0].slice(0, 2);
    }
  } else if (parts.length === 2) {
    if (parts[0].length === 1 && parts[1].length === 0) {
      parts[0] = '0' + parts[0];
    } else if (parts[0].length === 2 && parts[1].length === 3) {
      parts.push(parts[1].slice(2));
      parts[1] = parts[1].slice(0, 2);
    }
  } else if (parts.length === 3) {
    if (parts[1].length === 1 && parts[2].length === 0) {
      parts[1] = '0' + parts[1];
    }
  }

  return parts.join('/');
};

export const parsers = {
  numeric: numbersOnlyParse,
  phone: phoneNumberParse,
  birthday: birthdayParse
};

const fieldValidators = {
  required,
  trimmedRequired,
  number,
  email,
  trimmedEmail,
  emailFormat,
  trimmedEmailFormat,
  employeeEmail,
  alphaNumeric,
  phoneNumber,
  emailRequired,
  passwordRequired,
  password,
  confirmPassword,
  reduxConfirmPassword,
  displayName,
  trimmedDisplayName,
  rewardsPlsNumber,
  rewardsNumber,
  rewardsNumberFormat,
  rewardsPin,
  termsOfService,
  birthday,
  zipCode,
  reduxConfirmEmail,
  uuid,
  uuidFormat,
  uuidRequired
};

export default fieldValidators;
